import { useEffect, useState, useMemo, useRef, useContext } from "react";
import { AuthContext } from "../../../../contexts/AuthContext";
import type {
  ProspectResponse,
  PartialPositionPlayerMetrics,
  PartialPitcherMetrics,
  ProspectPositionPlayerMetricsResponse,
  ProspectPitcherMetricsResponse,
  TeamInfo,
} from "../../../../types/api";
import type { DefaultTagDefinition } from "../../../../types/tags";
import { TagSelectorModal } from "../../../../components/tags/TagSelectorModal";
import { JerseyNumberModal } from "../../../../components/JerseyNumberModal";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { pitchConfigsForSport } from "../../../../constants/pitchConfig";
import { toRosterSport } from "../../../../constants/sport";
import {
  formatPlayerLevel,
  playerLevelFromLabel,
} from "../../../../constants/playerLevels";
import { jucoYearFromLabel } from "../../../../constants/jucoYears";
import { formatJucoYear } from "../../../../utils/roster-class";
import {
  getDefaultTagDefinitions,
  getProspect,
  deleteProspect,
  levelNullFields,
} from "../../../../services/prospects";
import {
  removeProspectFromTeam,
  addProspectToTeam,
  createTeam,
  updateProspectTeam,
} from "../../../../services/teams";
import { TrashIcon } from "../../../../assets/TrashIcon";
import { AddTeamModal, type AddTeamResult } from "../../../../components/AddTeamModal";
import { getErrorMessage } from "../../../../utils/errorHandler";
import {
  parseHeight,
  formatHeight,
  formatPhoneNumber,
  stripPhoneFormat,
  formatDateForDisplay,
} from "../../../../utils/formatters";
import type { UpdateProspectProfileData } from "../../../../services/prospects";
import { ProspectMainViewer } from "./ProspectMainViewer";
import { ProspectEditViewer } from "./ProspectEditViewer";
import type { ProfileFormData, FormErrors } from "./edit/ProfileDetailsForm";
import type { AboutFormData } from "./edit/AboutDetailsForm";
import "./ProspectViewerPanel.css";

interface ProspectViewerPanelProps {
  prospectId: string | null;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Section to activate when the panel opens with this prospect. Used for
   * deep-links (e.g. Latest Notes → Notes). Null/undefined defaults to "profile".
   */
  initialSection?: "profile" | "videos" | "notes" | "about" | null;
  onFavoriteToggle: (
    prospectId: string,
    currentIsFavorite: boolean
  ) => Promise<void>;
  onTagUpdate: (
    prospectId: string,
    tagName: string,
    values: string[]
  ) => Promise<void>;
  onPositionPlayerMetricUpdate: (
    prospectId: string,
    field: string,
    value: number | null
  ) => Promise<void>;
  onPitcherMetricUpdate: (
    prospectId: string,
    field: string,
    value: number | null
  ) => Promise<void>;
  onProfileSave?: (
    prospectId: string,
    data: UpdateProspectProfileData
  ) => Promise<ProspectResponse>;
  onJerseyNumberUpdate?: (
    teamId: string,
    prospectId: string,
    jerseyNumber: number | null
  ) => Promise<void>;
  onProspectDeleted: (prospectId: string) => void;
}

export function ProspectViewerPanel({
  prospectId,
  isOpen,
  onClose,
  initialSection,
  onFavoriteToggle,
  onTagUpdate,
  onPositionPlayerMetricUpdate,
  onPitcherMetricUpdate,
  onProfileSave,
  onJerseyNumberUpdate,
  onProspectDeleted,
}: ProspectViewerPanelProps) {

  // Get current user from auth context
  const authContext = useContext(AuthContext);
  const currentUserId = authContext?.user?.profile?.id;
  const effectiveOrganizationId = authContext?.effectiveOrganizationId;

  // Prospect data
  const [prospect, setProspect] = useState<ProspectResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Local copy for optimistic updates
  const [localProspect, setLocalProspect] = useState<ProspectResponse | null>(
    null
  );

  // Tags state
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [activeTagName, setActiveTagName] = useState<string | null>(null);
  const [tagDefinitions, setTagDefinitions] = useState<DefaultTagDefinition[]>(
    []
  );
  const [tagError, setTagError] = useState<string | null>(null);

  // Jersey modal state
  const [jerseyModalVisible, setJerseyModalVisible] = useState(false);
  const [activeTeam, setActiveTeam] = useState<TeamInfo | null>(null);

  // Metrics state
  const [activeSection, setActiveSection] = useState<
    "profile" | "videos" | "notes" | "about"
  >(initialSection ?? "notes");

  // Read initialSection from a ref inside the fetchProspect effect so a late
  // prop change (parent clearing the deep-link after consumption) doesn't
  // retrigger that effect and refetch the prospect.
  const initialSectionRef = useRef(initialSection);
  useEffect(() => {
    initialSectionRef.current = initialSection;
  }, [initialSection]);

  // The fetch effect below only reruns on [isOpen, prospectId], so a section
  // requested while the panel is ALREADY open on this same prospect — the
  // table's video badge, when the coach opened that player a moment ago —
  // would otherwise be silently dropped. Apply it here instead. Deliberately
  // separate from the fetch effect so this cannot retrigger a prospect refetch.
  useEffect(() => {
    if (isOpen && initialSection) {
      setActiveSection(initialSection);
    }
  }, [isOpen, initialSection]);
  const [activeMetricsTab, setActiveMetricsTab] = useState<
    "positionPlayer" | "pitcher"
  >("positionPlayer");
  const [metricsError, setMetricsError] = useState<string | null>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState<"profile" | "about">(
    "profile"
  );
  const [profileFormData, setProfileFormData] = useState<ProfileFormData>({
    firstName: "",
    lastName: "",
    position: "",
    gradYear: "",
    heightFeet: "",
    heightInches: "",
    weight: "",
    bats: null,
    throws: null,
  });
  const [aboutFormData, setAboutFormData] = useState<AboutFormData>({
    phone: "",
    email: "",
    twitterUrl: "",
    instagramUrl: "",
    playerLevel: "",
    jucoYear: "",
    city: "",
    state: "",
    birthDate: "",
    visitDate: "",
    address: "",
    zipCode: "",
    parentName: "",
    parentPhone: "",
    coachName: "",
    coachPhone: "",
    advisorName: "",
    advisorPhone: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Add team modal state
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false);

  // Fetch prospect when panel opens or prospect ID changes
  useEffect(() => {
    const fetchProspect = async () => {
      if (!isOpen || !prospectId) {
        setProspect(null);
        setLocalProspect(null);
        setLoadError(null);
        setActiveSection(initialSectionRef.current ?? "notes"); // Notes is the default landing tab, matching mobile
        setActiveMetricsTab("positionPlayer"); // Reset to default tab
        setIsEditing(false); // Reset edit mode
        return;
      }

      setLoading(true);
      setLoadError(null);
      setActiveSection(initialSectionRef.current ?? "notes"); // Honor deep-link section when loading new prospect; otherwise default to Notes
      setActiveMetricsTab("positionPlayer"); // Reset to default tab when loading new prospect
      setIsEditing(false); // Reset edit mode when loading new prospect

      try {
        const data = await getProspect(prospectId);
        setProspect(data);
        setLocalProspect(data);
      } catch (error) {
        console.error("Failed to fetch prospect:", error);
        setLoadError("Failed to load prospect details");
      } finally {
        setLoading(false);
      }
    };

    fetchProspect();
  }, [isOpen, prospectId]);

  // Fetch tag definitions when panel opens or org changes
  useEffect(() => {
    const fetchTagDefinitions = async () => {
      try {
        const definitions = await getDefaultTagDefinitions();
        setTagDefinitions(definitions);
      } catch (error) {
        console.error("Failed to fetch tag definitions:", error);
      }
    };

    if (isOpen) {
      fetchTagDefinitions();
    }
  }, [isOpen, effectiveOrganizationId]);

  // Initialize form data when entering edit mode
  useEffect(() => {
    if (isEditing && localProspect) {
      const { feet, inches } = parseHeight(localProspect.height);

      setProfileFormData({
        firstName: localProspect.firstName || "",
        lastName: localProspect.lastName || "",
        position: localProspect.position || "",
        gradYear: localProspect.gradYear?.toString() || "",
        heightFeet: feet,
        heightInches: inches,
        weight: localProspect.weight?.toString() || "",
        bats: localProspect.bats || null,
        throws: localProspect.throws || null,
      });

      setAboutFormData({
        phone: formatPhoneNumber(localProspect.phone || ""),
        email: localProspect.email || "",
        twitterUrl: localProspect.about?.twitterUrl || "",
        instagramUrl: localProspect.about?.instagramUrl || "",
        // Top-level first, `about` as the fallback: the field moved off `about` and the old
        // column stays populated for one release. `??` rather than `||` so an ordinal 0 wins.
        playerLevel:
          formatPlayerLevel(
            localProspect.playerLevel ?? localProspect.about?.playerLevel,
          ) ?? "",
        jucoYear: formatJucoYear(localProspect.jucoYear) ?? "",
        city: localProspect.about?.city || "",
        state: localProspect.about?.state || "",
        birthDate: formatDateForDisplay(localProspect.about?.birthDate || ""),
        visitDate: formatDateForDisplay(localProspect.about?.visitDate || ""),
        address: localProspect.about?.address || "",
        zipCode: localProspect.about?.zipCode || "",
        parentName: localProspect.about?.parentName || "",
        parentPhone: formatPhoneNumber(localProspect.about?.parentPhone || ""),
        coachName: localProspect.about?.coachName || "",
        coachPhone: formatPhoneNumber(localProspect.about?.coachPhone || ""),
        advisorName: localProspect.about?.advisorName || "",
        advisorPhone: formatPhoneNumber(
          localProspect.about?.advisorPhone || ""
        ),
      });
    }
  }, [isEditing, localProspect]);

  // Handle ESC key to close panel or cancel edit
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        if (isEditing && !isSaving) {
          // Cancel edit mode
          handleCancelEdit();
        } else if (!isEditing) {
          // Close panel
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isEditing, isSaving, onClose]);

  // Transform tags to map
  const tagsByName = useMemo(() => {
    if (!localProspect?.defaultTags) return {};
    const map: Record<string, string[]> = {};
    for (const tag of localProspect.defaultTags) {
      if (!map[tag.tagName]) map[tag.tagName] = [];
      map[tag.tagName].push(tag.tagValue);
    }
    return map;
  }, [localProspect?.defaultTags]);

  // Get active tag definition for modal
  const activeTagDefinition = useMemo(() => {
    if (!activeTagName) return null;
    return tagDefinitions.find((def) => def.tagName === activeTagName) || null;
  }, [activeTagName, tagDefinitions]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFavoriteToggle = async () => {
    if (!localProspect) return;

    // Optimistic update
    setLocalProspect({
      ...localProspect,
      isFavorite: !localProspect.isFavorite,
    });

    try {
      await onFavoriteToggle(localProspect.id, localProspect.isFavorite);
    } catch (error) {
      // Revert on error
      setLocalProspect(prospect);
      console.error("Failed to toggle favorite:", error);
    }
  };

  const handleTagPress = (tagName: string) => {
    setActiveTagName(tagName);
    setTagModalVisible(true);
    setTagError(null);
  };

  const handleTagUpdate = async (values: string[]) => {
    if (!localProspect || !activeTagName) return;

    // Optimistic update
    const updatedTags =
      localProspect.defaultTags?.filter((t) => t.tagName !== activeTagName) ||
      [];
    const newTags = values.map((value) => ({
      id: `temp-${Date.now()}`,
      prospectId: localProspect.id,
      tagName: activeTagName,
      tagValue: value,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
    setLocalProspect({
      ...localProspect,
      defaultTags: [...updatedTags, ...newTags],
    });
    setTagModalVisible(false);
    setActiveTagName(null);

    try {
      await onTagUpdate(localProspect.id, activeTagName, values);
    } catch (error) {
      // Revert on error
      setLocalProspect(prospect);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update tag";
      setTagError(errorMessage);
    }
  };

  const handlePositionPlayerMetricUpdate = async (
    field: string,
    value: number | null
  ) => {
    if (!localProspect) return;

    // Optimistic update
    const updatedMetrics: PartialPositionPlayerMetrics = {
      ...(localProspect.positionPlayerMetrics || {}),
      [field]: value,
    };

    // Also optimistically add to metric entries if we have a value
    let updatedEntries = localProspect.metricEntries || [];
    if (value !== null) {
      const newEntry = {
        id: `temp-${Date.now()}`,
        metricName: field,
        metricCategory: 'position_player' as const,
        value,
        recordedByUserId: currentUserId || '',
        recordedByName: 'You',
        recordedAt: new Date().toISOString(),
      };
      updatedEntries = [newEntry, ...updatedEntries];
    }

    setLocalProspect({
      ...localProspect,
      positionPlayerMetrics: updatedMetrics as ProspectPositionPlayerMetricsResponse,
      metricEntries: updatedEntries,
    });

    try {
      await onPositionPlayerMetricUpdate(localProspect.id, field, value);
    } catch (error) {
      // Revert on error
      setLocalProspect(prospect);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update metric";
      setMetricsError(errorMessage);
      setTimeout(() => setMetricsError(null), 3000);
    }
  };

  const handlePitcherMetricUpdate = async (
    field: string,
    value: number | null
  ) => {
    if (!localProspect) return;

    // Optimistic update for metrics
    const updatedMetrics: PartialPitcherMetrics = {
      ...(localProspect.pitcherMetrics || {}),
      [field]: value,
    };

    // Also optimistically add to metric entries if we have a value
    let updatedEntries = localProspect.metricEntries || [];
    if (value !== null) {
      const newEntry = {
        id: `temp-${Date.now()}`,
        metricName: field,
        metricCategory: 'pitcher' as const,
        value,
        recordedByUserId: currentUserId || '',
        recordedByName: 'You',
        recordedAt: new Date().toISOString(),
      };
      updatedEntries = [newEntry, ...updatedEntries];
    }

    setLocalProspect({
      ...localProspect,
      pitcherMetrics: updatedMetrics as ProspectPitcherMetricsResponse,
      metricEntries: updatedEntries,
    });

    try {
      await onPitcherMetricUpdate(localProspect.id, field, value);
    } catch (error) {
      // Revert on error
      setLocalProspect(prospect);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update metric";
      setMetricsError(errorMessage);
      setTimeout(() => setMetricsError(null), 3000);
    }
  };

  // Jersey number handlers
  const handleJerseyClick = (team: TeamInfo) => {
    setActiveTeam(team);
    setJerseyModalVisible(true);
  };

  const handleJerseySave = async (jerseyNumber: number | null) => {
    if (!localProspect || !activeTeam || !onJerseyNumberUpdate) return;

    // Optimistic update
    const updatedTeams = localProspect.teams.map((t) =>
      t.id === activeTeam.id ? { ...t, jerseyNumber } : t
    );
    setLocalProspect({ ...localProspect, teams: updatedTeams });
    setJerseyModalVisible(false);
    setActiveTeam(null);

    try {
      await onJerseyNumberUpdate(activeTeam.id, localProspect.id, jerseyNumber);
    } catch (error) {
      // Revert on error
      setLocalProspect(prospect);
      console.error("Failed to update jersey number:", error);
    }
  };

  // Remove from team handler
  const handleRemoveTeam = async (teamIdToRemove: string) => {
    if (!localProspect) return;

    // Confirm before removing
    const team = localProspect.teams.find(t => t.id === teamIdToRemove);
    if (!team) return;

    const confirmed = window.confirm(
      `Are you sure you want to remove ${localProspect.firstName} ${localProspect.lastName} from ${team.name}?`
    );
    if (!confirmed) return;

    // Optimistic update
    const updatedTeams = localProspect.teams.filter((t) => t.id !== teamIdToRemove);
    setLocalProspect({ ...localProspect, teams: updatedTeams });

    try {
      await removeProspectFromTeam(teamIdToRemove, localProspect.id);
    } catch (error) {
      // Revert on error
      setLocalProspect(prospect);
      console.error("Failed to remove from team:", error);
    }
  };

  // Add to team handler
  const handleAddTeam = async (result: AddTeamResult) => {
    const { team, jerseyNumber } = result;
    if (!localProspect || !team.name) return;

    setIsAddTeamModalOpen(false);

    try {
      let teamId = team.id;

      // If it's a new team (or doesn't have an ID), create it first
      if (team.isNew || !teamId) {
        const createResponse = await createTeam(team.name);
        teamId = createResponse.id;
      }

      // Add the existing prospect to the team
      const response = await addProspectToTeam(teamId as string, localProspect.id);

      // If jersey number was provided, update it
      if (jerseyNumber !== null) {
        await updateProspectTeam(response.teamId, localProspect.id, { jerseyNumber });
      }

      // Optimistic update
      const newTeam = {
        id: response.teamId,
        name: response.teamName,
        jerseyNumber: jerseyNumber,
      };
      setLocalProspect({
        ...localProspect,
        teams: [...localProspect.teams, newTeam],
      });
    } catch (error) {
      console.error("Failed to add to team:", error);
      const message = error instanceof Error ? error.message : "Failed to add to team";
      alert(message.includes("already on") ? "Already on this team" : message);
      // Reload to get correct state
      if (prospect) {
        setLocalProspect(prospect);
      }
    }
  };

  // Edit mode handlers
  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    if (
      !profileFormData.firstName.trim() &&
      !profileFormData.lastName.trim()
    ) {
      errors.firstName = "Enter at least a first or last name";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setActiveEditTab("profile");
    setFormErrors({});
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormErrors({});
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!localProspect || !onProfileSave) return;

    if (!validateForm()) {
      if (formErrors.firstName || formErrors.lastName) {
        setActiveEditTab("profile");
      }
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const playerLevel = playerLevelFromLabel(aboutFormData.playerLevel);

      const height = formatHeight(
        profileFormData.heightFeet,
        profileFormData.heightInches
      );

      // Collect top-level nullable fields the user has cleared
      const nullFields: string[] = [];
      if (!aboutFormData.phone) nullFields.push("phone");
      if (!aboutFormData.email) nullFields.push("email");
      if (!profileFormData.position) nullFields.push("position");
      if (!profileFormData.gradYear) nullFields.push("gradYear");
      if (!profileFormData.heightFeet && !profileFormData.heightInches)
        nullFields.push("height");
      if (!profileFormData.weight) nullFields.push("weight");
      if (!profileFormData.bats) nullFields.push("bats");
      if (!profileFormData.throws) nullFields.push("throws");
      nullFields.push(...levelNullFields(playerLevel));

      const updateData: UpdateProspectProfileData = {
        firstName: profileFormData.firstName,
        lastName: profileFormData.lastName,
        position: profileFormData.position || undefined,
        gradYear: profileFormData.gradYear
          ? Number(profileFormData.gradYear)
          : undefined,
        height,
        weight: profileFormData.weight
          ? Number(profileFormData.weight)
          : undefined,
        bats: profileFormData.bats || undefined,
        throws: profileFormData.throws || undefined,
        phone: aboutFormData.phone
          ? stripPhoneFormat(aboutFormData.phone)
          : undefined,
        email: aboutFormData.email || undefined,
        // Sent at top level and inside `about` both: the column moved, and which one the server
        // reads depends on which side of the API deploy this request lands on.
        playerLevel,
        // Omitted rather than sent empty for a non-JUCO player: `levelNullFields` above has
        // already put `jucoYear` in `nullFields`, which is the only thing that clears a scalar.
        jucoYear: jucoYearFromLabel(aboutFormData.jucoYear) ?? undefined,
        nullFields: nullFields.length > 0 ? nullFields : undefined,
        about: {
          twitterUrl: aboutFormData.twitterUrl || null,
          instagramUrl: aboutFormData.instagramUrl || null,
          playerLevel,
          city: aboutFormData.city || null,
          state: aboutFormData.state || null,
          birthDate: aboutFormData.birthDate || null,
          visitDate: aboutFormData.visitDate || null,
          address: aboutFormData.address || null,
          zipCode: aboutFormData.zipCode || null,
          parentName: aboutFormData.parentName || null,
          parentPhone: aboutFormData.parentPhone
            ? stripPhoneFormat(aboutFormData.parentPhone)
            : null,
          coachName: aboutFormData.coachName || null,
          coachPhone: aboutFormData.coachPhone
            ? stripPhoneFormat(aboutFormData.coachPhone)
            : null,
          advisorName: aboutFormData.advisorName || null,
          advisorPhone: aboutFormData.advisorPhone
            ? stripPhoneFormat(aboutFormData.advisorPhone)
            : null,
        },
      };

      const updatedProspect = await onProfileSave(localProspect.id, updateData);

      // Update local state with the saved data
      setProspect(updatedProspect);
      setLocalProspect(updatedProspect);

      setIsEditing(false);
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to save changes");
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
    setDeleteError(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!localProspect || isDeleting) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteProspect(localProspect.id);
      setIsDeleteModalOpen(false);
      onProspectDeleted(localProspect.id);
    } catch (err) {
      setDeleteError(
        `Failed to delete prospect: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // A softball program grades a different arsenal than a baseball one. `toRosterSport` reads
  // anything other than 'softball' — including an org with no sport recorded — as baseball, so
  // nothing changes for the organizations that were here first.
  //
  // The coach's OWN organization is the normal source. `activeOrganization` is only ever set by
  // a system admin impersonating an org (AuthContext nulls it for every other role), so reading
  // it alone would show every real softball coach the baseball set — it has to come second, as
  // the impersonation override it actually is.
  // Branch on whether an impersonation is ACTIVE, not on whether it carries a sport. `sport` is
  // nullable, so `?? profile.sport` would let a system admin whose own org is softball impersonate
  // a sport-less org and be shown the softball set on that org's prospects — writing softball
  // fields onto baseball records.
  const rosterSport = toRosterSport(
    authContext?.activeOrganization
      ? authContext.activeOrganization.sport
      : authContext?.user?.profile?.sport,
  );
  const pitchConfigs = pitchConfigsForSport(rosterSport);

  return (
    <>
      <div
        className={`prospect-viewer-panel-overlay ${
          isOpen ? "prospect-viewer-panel-overlay--open" : ""
        }`}
        onClick={handleOverlayClick}
      />
      <div
        className={`prospect-viewer-panel ${
          isOpen ? "prospect-viewer-panel--open" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prospect-viewer-title"
        aria-hidden={!isOpen}
        // When closed the panel stays mounted (for the slide transition), so
        // `inert` removes its controls from tab order and the a11y tree.
        inert={!isOpen}
      >
        <div className="prospect-viewer-panel__content">
          {/* Loading State */}
          {loading && (
            <div className="prospect-viewer-panel__loading">
              <p>Loading prospect details...</p>
            </div>
          )}

          {/* Error State */}
          {loadError && !loading && (
            <div className="prospect-viewer-panel__error">
              <p>{loadError}</p>
              <button onClick={onClose}>Close</button>
            </div>
          )}

          {/* Content */}
          {!loading && !loadError && localProspect && (
            <>
              {!isEditing ? (
                <ProspectMainViewer
                  prospect={localProspect}
                  activeSection={activeSection}
                  onSectionChange={setActiveSection}
                  onFavoriteToggle={handleFavoriteToggle}
                  onEditClick={handleEditClick}
                  onClose={onClose}
                  activeMetricsTab={activeMetricsTab}
                  onMetricsTabChange={setActiveMetricsTab}
                  tagDefinitions={tagDefinitions}
                  tagsByName={tagsByName}
                  onTagPress={handleTagPress}
                  tagError={tagError}
                  metricsError={metricsError}
                  onPositionPlayerMetricUpdate={handlePositionPlayerMetricUpdate}
                  onPitcherMetricUpdate={handlePitcherMetricUpdate}
                  pitchConfigs={pitchConfigs}
                  currentUserId={currentUserId}
                  sport={authContext?.activeOrganization?.sport}
                  rosterSport={rosterSport}
                  onJerseyClick={handleJerseyClick}
                  onRemoveTeam={handleRemoveTeam}
                  onAddTeam={() => setIsAddTeamModalOpen(true)}
                />
              ) : (
                <ProspectEditViewer
                  activeEditTab={activeEditTab}
                  onEditTabChange={setActiveEditTab}
                  profileFormData={profileFormData}
                  aboutFormData={aboutFormData}
                  formErrors={formErrors}
                  onProfileFormChange={(data) =>
                    setProfileFormData({ ...profileFormData, ...data })
                  }
                  onAboutFormChange={(data) =>
                    setAboutFormData({ ...aboutFormData, ...data })
                  }
                  onClearFormError={(field) =>
                    setFormErrors({ ...formErrors, [field]: undefined })
                  }
                  isSaving={isSaving}
                  saveError={saveError}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  onDeleteClick={handleDeleteClick}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Tag Selector Modal */}
      <TagSelectorModal
        visible={tagModalVisible}
        definition={activeTagDefinition}
        selectedValues={activeTagName ? tagsByName[activeTagName] || [] : []}
        onSave={handleTagUpdate}
        onClose={() => {
          setTagModalVisible(false);
          setActiveTagName(null);
        }}
      />

      {/* Jersey Number Modal */}
      {localProspect && (
        <JerseyNumberModal
          isOpen={jerseyModalVisible}
          teamId={activeTeam?.id ?? ''}
          teamName={activeTeam?.name ?? ''}
          prospectName={`${localProspect.firstName} ${localProspect.lastName}`}
          currentJerseyNumber={activeTeam?.jerseyNumber ?? null}
          onSave={handleJerseySave}
          onClose={() => {
            setJerseyModalVisible(false);
            setActiveTeam(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Prospect"
        message={
          localProspect
            ? `Are you sure you want to delete ${localProspect.firstName} ${localProspect.lastName}? This action cannot be undone.`
            : "Are you sure you want to delete this prospect? This action cannot be undone."
        }
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        icon={<TrashIcon />}
      />

      {/* Delete Error Message */}
      {deleteError && (
        <div className="prospect-viewer-panel__error-message">
          {deleteError}
        </div>
      )}

      {/* Add Team Modal */}
      {localProspect && (
        <AddTeamModal
          isOpen={isAddTeamModalOpen}
          prospectName={`${localProspect.firstName} ${localProspect.lastName}`}
          onSave={handleAddTeam}
          onClose={() => setIsAddTeamModalOpen(false)}
        />
      )}
    </>
  );
}
